<script lang="ts" setup>
import type {
  JobFilterProperty,
  JobFilterCustomDataProperty,
  JobFilterComparison,
  JobFilterValue,
} from '@ironsight/types'
import { computed, PropType } from 'vue'
import JobFilterChip from '@/components/jobs/job-search-page/JobFilterChip.vue'
import JobFilterChipMenuDropdown from '@/components/jobs/job-search-page/JobFilterChipMenuDropdown.vue'
import { useFilterContextBridge } from '@/components/filters/useFilterContextBridge'

const props = defineProps({
  expandedChipId: {
    type: String as PropType<string | null>,
    default: null,
  },
  filterableProperties: {
    type: Array as PropType<JobFilterProperty[]>,
    required: true,
  },
})

const emit = defineEmits<{
  (e: 'toggle-chip', chipId: string): void
  (e: 'remove-chip', chipId: string): void
  (e: 'operation-change', comparison: JobFilterComparison): void
  (e: 'update-value', value: JobFilterValue): void
  (e: 'apply-filter'): void
}>()

const { store } = useFilterContextBridge()
const { getVisibleFilters, getVisibleFilterById } = store

const handleToggleChip = (chipId: string) => {
  emit('toggle-chip', chipId)
}

const handleRemoveChip = (chipId: string) => {
  emit('remove-chip', chipId)
}

const handleUpdateValue = (value: any) => {
  if (!props.expandedChipId) return
  emit('update-value', value)
}

const handleApplyFilter = () => {
  emit('apply-filter')
}

const handleMenuClose = (isOpen: boolean, chipId: string) => {
  if (!isOpen && props.expandedChipId === chipId) {
    handleApplyFilter()
  }
}

const getFilterableProperty = computed(() => {
  if (props.expandedChipId === null) {
    return null
  }

  const filter = getVisibleFilterById.value(props.expandedChipId)
  if (filter) {
    if (filter.property === 'CustomData') {
      return props.filterableProperties.find(
        (prop) =>
          (prop as JobFilterCustomDataProperty).key &&
          (prop as JobFilterCustomDataProperty).key === filter.key,
      )
    }
    return props.filterableProperties.find((prop) => prop.name === filter.property)
  }
  return null
})
</script>

<template>
  <v-row class="w-full items-center gap-2" dense>
    <v-col v-for="filter in getVisibleFilters" :key="filter.id" class="relative" cols="auto">
      <div :id="`chip-wrap-${filter.id}`">
        <job-filter-chip
          :filterable-properties="props.filterableProperties"
          :chip-id="filter.id"
          @toggle="() => handleToggleChip(filter.id)"
          @remove="handleRemoveChip(filter.id)"
        />
      </div>

      <v-menu
        rounded="xl"
        :value="expandedChipId === filter.id"
        :activator="`#chip-wrap-${filter.id}`"
        offset-y
        :close-on-content-click="false"
        max-width="600"
        min-width="300"
        :nudge-bottom="4"
        @input="(isOpen) => handleMenuClose(isOpen, filter.id)"
      >
        <job-filter-chip-menu-dropdown
          v-if="expandedChipId === filter.id"
          :filter-type="filter.type"
          :subject="filter.lookupEntity ?? filter.property"
          :defined-options="filter.definedOptions"
          :value="filter.value"
          :can-be-empty="getFilterableProperty?.canBeEmpty ?? false"
          :label="getFilterableProperty?.label"
          :comparison="filter.comparison"
          :supported-comparisons="getFilterableProperty?.supportedComparisons"
          @input="handleUpdateValue"
          @apply="handleApplyFilter"
          @operation-change="emit('operation-change', $event)"
        />
      </v-menu>
    </v-col>
  </v-row>
</template>
